import { useEffect, type JSX } from 'react';
import { useAtomReducer, useSelector } from 'src/hooks';
import { dispatchWidgetEvent } from 'src/events/eventDispatcher';
import { contactPersonToCustomer } from 'src/events/customer';
import {
    initializeHiddenBodyStyle,
    useHiddenBody as useHiddenBody,
} from 'src/hooks/common/ui/useHiddenBody';
import { useWidgetEventEffect } from 'src/hooks/domain/events/useWidgetEventEffect';
import { useCartCheckoutContext } from './CartCheckoutContext';
import { CartCheckoutTabs } from './CartCheckoutTabs';
import { voucherAtom, resetIgnoredValueCards } from 'src/state/voucher/voucherAtom';
import { cartAtom, selectCartComposition } from 'src/state/cart/cartAtom';
import { validateEvent } from 'src/state/cart/cart.reducer';
import { checkoutInfoAtom } from 'src/state/checkout-info/checkoutInfoAtom';
import { route } from 'src/components/common/router/Router';

const hiddenBodyStyle = initializeHiddenBodyStyle('activities');

export function Cart(): JSX.Element {
    const [cartItems, cartDispatch] = useAtomReducer(cartAtom);
    const [{ appliedPromoCode }, voucherDispatch] = useAtomReducer(voucherAtom);
    const cartComposition = useSelector(cartItems, selectCartComposition);
    const { onConfirmationPage } = useCartCheckoutContext();

    useEffect(() => {
        // Reset ignored value cards when the checkout process starts
        voucherDispatch(resetIgnoredValueCards());
        cartDispatch(validateEvent()); // Makes sure everything in cart is valid and not past cutoff date
    }, [cartDispatch, voucherDispatch]);

    useEffect(() => {
        // Don't auto-redirect if we're on the confirmation page (payment just completed)
        if (onConfirmationPage) return;

        switch (cartComposition) {
            case 'waitingListOnly':
                route('/waitlist');
                break;
            case 'regularOnly':
                route('/checkout');
                break;
        }
    }, [cartComposition, onConfirmationPage]);

    useEffect(() => {
        if (cartComposition !== 'waitingListOnly' && !onConfirmationPage)
            dispatchWidgetEvent({
                eventType: 'startCheckout',
                cartItems: Object.values(cartAtom.subject.value),
            });
    }, [cartComposition, onConfirmationPage]);

    const { paymentRedirectContext, appliedGiftcards } = useCartCheckoutContext();

    useHiddenBody(hiddenBodyStyle);

    // Dispatch book event if redirected from Vipps)
    useWidgetEventEffect(
        (cartItems, giftCards, promoCode) => ({
            eventType: 'book',
            cartItems: Object.values(cartItems),
            giftCards: giftCards
                ? giftCards.map((giftCard) => ({
                      priceReduction: giftCard.priceReduction,
                      giftcardReference: giftCard.giftcardStatus.id,
                  }))
                : undefined,
            promoCode: promoCode,
            customer: contactPersonToCustomer(checkoutInfoAtom.subject.value?.contactPerson),
        }),
        cartItems,
        appliedGiftcards,
        appliedPromoCode,
        () => !paymentRedirectContext,
    );

    return <CartCheckoutTabs></CartCheckoutTabs>;
}
